import {
  AloftPrice,
  AloftPriceJSON,
  isDefined,
  orUndefined,
  schAloftPrice,
} from '@aloft/toolkit-shared';
import { Block, Input, ProductColor, Tooltip, theme } from '@aloft/toolkit-sui';

import { useRef, useState } from 'react';
import { Controller, ControllerRenderProps, FieldPath, useWatch } from 'react-hook-form';

import { dispatchAdjustmentsPageAlertTrigger } from '../../../modules/alerts/dispatchAdjustmentsPageAlertTrigger.ts';
import { useAlertsRef } from '../../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectCompAdjustments } from '../../../redux/features/selectors/adjustmentSelectors.ts';
import { selectCommentaryV2 } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { PROPERTY_GRID_ROW_HEIGHT } from '../consts.ts';
import { PropertyGridForm } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { usePropertyGridCellFocus } from './shared/usePropertyGridCellFocus.ts';
import {
  alertTargetFieldHoverSx,
  fieldTypographyProps,
  getAlertTargetInputFieldSx,
} from './styles.ts';
import { PropertyGridFieldProps } from './types.ts';
import { getCompAdjustmentValue, stripDollarSign } from './utils.tsx';

export const AdjustmentField = ({
  columnIndex,
  field,
  control,
  getValues,
}: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();
  const alertsRef = useAlertsRef();
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const { manualAdjustmentKey } = field;

  if (!manualAdjustmentKey) {
    return (
      <Input
        type="text"
        value=""
        isDisabled
        onChange={() => {}}
        onClear={() => {}}
        __dangerDoNotUseSx={fieldTypographyProps.__dangerDoNotUseSx}
      />
    );
  }

  const fieldName =
    `columns.${columnIndex}.propertyData.editableData.manualAdjustments.${manualAdjustmentKey}` as FieldPath<PropertyGridForm>;

  const manualAdjustmentOverride = useWatch({ control, name: fieldName }) as
    | AloftPriceJSON
    | undefined;
  const propertyId = useWatch({ control, name: `columns.${columnIndex}.propertyId` });

  const fieldRef = useRef<HTMLInputElement>(null);
  const {
    isAlertHighlighted,
    isAlertStyled,
    alertStyleVariant,
    alertCellTooltip,
    isAlertToastVisible,
    clearAlertTarget,
  } = usePropertyGridCellFocus({
    cellKeyId: manualAdjustmentKey,
    propertyId,
    fieldRef,
  });

  const compAdjustments = useAppSelector(selectCompAdjustments)[propertyId];
  let adjustmentValue = getCompAdjustmentValue(manualAdjustmentKey, compAdjustments);

  if (
    (manualAdjustmentKey === 'functionalUtility' ||
      manualAdjustmentKey === 'externalObsolescence') &&
    adjustmentValue.unitAmount > 0
  ) {
    adjustmentValue = adjustmentValue.mul(-1);
  }

  // Local state to track raw input value while typing
  const [rawInputValue, setRawInputValue] = useState<string | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);

  const handleSetFieldValue = (onChange: ControllerRenderProps['onChange'], newValue?: string) => {
    const parsedNewValue = orUndefined(AloftPrice.fromDollarLike)(newValue);
    if (parsedNewValue?.equals(adjustmentValue)) return;

    const parsedNewValueJSON = parsedNewValue?.toJSON();
    onChange(parsedNewValueJSON);
    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
    clearAlertTarget();
    dispatchAdjustmentsPageAlertTrigger(dispatch, alertsRef, alertsCommentary);
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { onChange, ref } }) => {
        const displayValue = (
          orUndefined(schAloftPrice.parse)(manualAdjustmentOverride) ?? adjustmentValue
        )?.toString(true);

        const handleFocus = () => {
          setIsFocused(true);
          // Remove $ symbol when focusing for easier editing (handles both "$100" and "-$100")
          const valueWithoutDollar = displayValue ? stripDollarSign(displayValue) : '';
          setRawInputValue(valueWithoutDollar);
        };

        const handleBlur = () => {
          setIsFocused(false);

          const inputToParse = rawInputValue !== undefined ? rawInputValue : displayValue;

          // Handle empty or zero cases
          const trimmedInput = inputToParse.trim();

          try {
            if (
              !inputToParse ||
              inputToParse.trim() === '' ||
              trimmedInput === '-$0' ||
              trimmedInput === '-$' ||
              trimmedInput === '-' ||
              AloftPrice.fromDollarLike(inputToParse).equals(AloftPrice.zero)
            ) {
              handleSetFieldValue(onChange, '$0');
            } else {
              handleSetFieldValue(onChange, inputToParse);
            }
          } catch {
            handleSetFieldValue(onChange, displayValue);
          }

          setRawInputValue(undefined);
        };

        const handleChange = (v: string) => setRawInputValue(v.replace(/^0+/, ''));

        const handleKeyDown = (e: React.KeyboardEvent) => {
          if (e.key === 'Escape') {
            setRawInputValue(undefined);
            setIsFocused(false);
            onChange(adjustmentValue?.toJSON() ?? undefined);
          } else if (e.key === 'Enter' || e.key === 'NumpadEnter') {
            (e.target as HTMLInputElement).blur();
          }
        };

        const inputValue =
          isFocused && isDefined(rawInputValue) ? rawInputValue : (displayValue ?? '');

        const alertTargetSx = getAlertTargetInputFieldSx(
          isAlertStyled,
          alertStyleVariant,
          isAlertHighlighted,
        );
        const alertBorderColor =
          alertStyleVariant === 'error' ? theme.colors.alertError : theme.colors.alertWarning;
        const showAlertEmphasis = Boolean(alertTargetSx && (isAlertHighlighted || isFocused));
        const alertInsetBoxShadow = `inset 0 0 0 2px ${alertBorderColor}`;
        const alertEmphasisBoxShadow = `${alertInsetBoxShadow}, ${alertTargetFieldHoverSx.boxShadow}`;
        const alertEmphasisSx = showAlertEmphasis
          ? {
              backgroundColor: alertTargetSx?.backgroundColor,
              filter: alertTargetFieldHoverSx.filter,
              boxShadow: alertEmphasisBoxShadow,
            }
          : undefined;

        const input = (
          <Input
            ref={(el) => {
              ref(el);
              (fieldRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
            }}
            type="text"
            value={inputValue}
            __dangerDoNotUseSx={{
              boxSizing: 'border-box',
              width: '100%',
              height: PROPERTY_GRID_ROW_HEIGHT,
              padding: '4px 8px',
              border: manualAdjustmentOverride
                ? `1px dashed ${theme.colors.buttonDefault}`
                : 'none',
              outline: 'none',
              borderRadius: '0',
              fontSize: '14px',
              textAlign: 'right',
              backgroundColor: alertTargetSx?.backgroundColor ?? theme.colors.textOffset,
              ...alertEmphasisSx,
              color: adjustmentValue?.getDisplayColor() as ProductColor | undefined,
              _focusVisible: alertTargetSx
                ? {
                    backgroundColor: alertTargetSx.backgroundColor,
                    filter: alertTargetFieldHoverSx.filter,
                    boxShadow: alertEmphasisBoxShadow,
                    outline: 'none',
                  }
                : {
                    outline: '1.5px solid',
                    outlineOffset: '-1.5px',
                    outlineColor: theme.colors.buttonDefault,
                    background: theme.colors.surface3,
                  },
              _hover: alertTargetSx
                ? {
                    backgroundColor: alertTargetSx.backgroundColor,
                    filter: alertTargetFieldHoverSx.filter,
                    boxShadow: alertEmphasisBoxShadow,
                  }
                : {
                    outline: '1.5px solid',
                    outlineOffset: '-1.5px',
                    outlineColor: theme.colors.buttonDefault,
                  },
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            onClear={() => {
              setRawInputValue('');
              onChange(AloftPrice.zero.toJSON());
            }}
            onKeyDown={handleKeyDown}
          />
        );

        if (!alertCellTooltip || !isAlertToastVisible) {
          return input;
        }

        return (
          <Tooltip label={alertCellTooltip} placement="top" hasArrow>
            <Block width="100%" height={PROPERTY_GRID_ROW_HEIGHT}>
              {input}
            </Block>
          </Tooltip>
        );
      }}
    />
  );
};
